import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import { useHistory, useRouteMatch } from 'react-router-dom';
import SanitizedText from 'sanitize-text/components/SanitizedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import getLang from 'I18n/utils/getLang';
import H4 from 'UIComponents/elements/headings/H4';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
// @ts-expect-error untyped module
import UIEmbeddedComponent from 'ui-addon-iframeable/embed/UIEmbeddedComponent';
import { useAppCollectionItems } from 'marketplace-ui-apps-core/collections/hooks/useAppCollectionItems';
import { useCollections } from 'marketplace-ui-common/collections/hooks/useCollections';
import { usePrevious } from 'marketplace-ui-common/hooks/usePrevious';
import LoadingWrapper from 'marketplace-ui-common/layout/components/LoadingWrapper';
import * as RequestState from 'marketplace-ui-common/requestStates/RequestState';
import { isFailed, isFulfilled, isSucceeded } from 'marketplace-ui-common/requestStates/RequestStateUtils';
import { interpolate } from 'marketplace-ui-common/i18n/utils';
import MetaDescription from 'marketplace-ui-common/components/elements/MetaDescription';
import MetaOpenGraph from 'marketplace-ui-common/components/elements/MetaOpenGraph';
import MetaRobots from 'marketplace-ui-common/components/elements/MetaRobots';
import MetaTwitter from 'marketplace-ui-common/components/elements/MetaTwitter';
import DocumentHead from 'marketplace-ui-common/components/portals/DocumentHead';
import PrerenderReady from 'marketplace-ui-common/components/prerender/PrerenderReady';
// @ts-expect-error untyped module
import { setDetailsReferrer } from '../../actions/DetailsActions';
// @ts-expect-error untyped module
import { searchApps } from '../../clients/ecosystemClient';
// @ts-expect-error untyped module
import { DEFAULT_SEARCH_LENGTH } from '../../constants';
import * as FilterKey from '../../constants/search/FilterKey';
// @ts-expect-error untyped module
import SearchResult from '../../records/SearchResult';
// @ts-expect-error untyped module
import { PublicOnly } from '../../selectors/auth';
// @ts-expect-error untyped module
import { getFullHref } from '../../utils/history';
import { useSocialScreenshotUrl } from '../../utils/hooks/useSocialScreenshot';
// @ts-expect-error untyped module
import { getCollectionPreviewRoute, getPathPagination } from '../../utils/urls';
import StorefrontFiltersContext from '../../context/StorefrontFiltersContext';
import { useAppContext } from '../../context/AppContext.tsx';
import StorefrontUsageTrackerContext from '../../context/StorefrontUsageTrackerContext';
// @ts-expect-error untyped module
import CanonicalLink from '../../components/elements/CanonicalLink';
// @ts-expect-error untyped module
import GenericError from '../../components/error/GenericError';
// @ts-expect-error untyped module
import ResultsPage from '../../components/storefront/results/ResultsPage';
import { getAppRecommendationTypes } from '../../utils/usageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const EMPTY_COLLECTION_SEARCH_RESULT = new SearchResult();
const SEARCH_LENGTH = DEFAULT_SEARCH_LENGTH;
const MAX_COLLECTION_ITEMS_LENGTH = 90;
const ErrorComponent = () => /*#__PURE__*/_jsx(NavMarker, {
  name: "STOREFRONT_ERROR_LOAD",
  children: /*#__PURE__*/_jsx(GenericError, {})
});
const LoadingComponent = () => /*#__PURE__*/_jsx(UILoadingSpinner, {
  className: "justify-center"
});
const StorefrontCollectionContainer = ({
  onReady
}) => {
  const {
    params: {
      collection: collectionSlug,
      collectionId: previewCollectionId,
      page
    },
    url
  } = useRouteMatch();
  const reduxDispatch = useDispatch();
  const prevCollectionSlug = usePrevious(collectionSlug);
  const prevPage = usePrevious(page);
  const history = useHistory();
  const appContext = useAppContext();
  const isAdminPreview = appContext === 'admin-preview';
  const {
    filters,
    clearFilters,
    onPageChange,
    onSortChange,
    updateAllFilters,
    updateFilter
  } = useContext(StorefrontFiltersContext);
  const {
    getCollectionById,
    getCollectionBySlug
  } = useCollections({
    appContext,
    offeringType: 'APP'
  });
  const collection = isAdminPreview ? getCollectionById(parseInt(previewCollectionId, 10)) : getCollectionBySlug(collectionSlug);
  const collectionId = collection ? collection.id : 0;
  const collectionListing = collection && (isAdminPreview ? collection.draftListing : collection.publishedListing);
  const collectingListingId = collectionListing ? collectionListing.id : undefined;
  const [requestState, setRequestState] = useState(RequestState.PENDING);
  const requestFulfilled = isFulfilled(requestState);
  const requestSucceeded = isSucceeded(requestState);
  const [searchResult, setSearchResult] = useState(EMPTY_COLLECTION_SEARCH_RESULT);
  const {
    items: searchResultItems,
    total: searchResultTotal
  } = searchResult;
  const pageCount = Math.ceil(searchResultTotal / SEARCH_LENGTH);
  const activePage = page ? parseInt(page, 10) : 1;
  const hasNextPage = activePage < pageCount;
  const hasPrevPage = activePage > 1;
  const offset = (activePage - 1) * SEARCH_LENGTH;
  const searchResultItemsTotal = searchResultItems.size;
  const hasRecommendations = searchResultItems.some(result => result.recommendation);
  const recommendationTypes = getAppRecommendationTypes(searchResultItems);
  const {
    data: collectionItemsData,
    offeringIds: collectionOfferingIds,
    requestState: collectionItemsRequestState
  } = useAppCollectionItems({
    appContext,
    collectionId,
    language: getLang(),
    length: MAX_COLLECTION_ITEMS_LENGTH,
    offset: 0
  });
  const [pageViewTracked, setPageViewTracked] = useState(false);
  const {
    trackInteraction,
    trackPageView,
    trackResultClick
  } = useContext(StorefrontUsageTrackerContext);
  const handlePageChange = useCallback((newPage, newFilters) => {
    if (isAdminPreview) {
      history.push(getPathPagination(getCollectionPreviewRoute(previewCollectionId), newPage));
    } else {
      onPageChange(newPage, newFilters);
    }
  }, [history, isAdminPreview, onPageChange, previewCollectionId]);
  const handleResultClick = useCallback((result, index) => {
    const eventProperties = {
      applicationId: parseInt(result.sourceId, 10),
      totalResults: searchResultItemsTotal,
      isRecommended: !!result.recommendation
    };
    trackResultClick(result, index, eventProperties);
    reduxDispatch(setDetailsReferrer(`collection::${collectionId}${collectingListingId ? `_${collectingListingId}` : ''}`));
  }, [collectingListingId, collectionId, reduxDispatch, searchResultItemsTotal, trackResultClick]);
  const canonicalUrlPath = `/${collectionSlug}`;
  const documentTitle = collectionListing ? interpolate(collectionListing.htmlPageTitle, {
    count: searchResultTotal
  }) : '';
  const metaDescription = collectionListing ? collectionListing.metaDescription : '';
  const noIndexNoFollow = requestSucceeded && searchResultTotal < 3;
  const prerenderStatusCode = requestSucceeded && !searchResultTotal ? 404 : 200;
  const language = getLang();
  useEffect(() => {
    if (isSucceeded(collectionItemsRequestState) && collectionItemsData) {
      if (isAdminPreview) {
        setSearchResult(new SearchResult({
          items: collectionItemsData.items.slice(0),
          total: Math.min(collectionItemsData.total, SEARCH_LENGTH)
        }));
        setRequestState(RequestState.SUCCEEDED);
      } else {
        setRequestState(RequestState.PENDING);
        searchApps({
          language,
          queries: [filters.toSearchQuery({
            length: SEARCH_LENGTH,
            offset,
            offeringIds: collectionOfferingIds
          })],
          showPreferredLanguageFirst: filters.get(FilterKey.SHOW_PREFERRED_LANG_FIRST)
        }).then(({
          results: [filteredResults]
        }) => {
          setSearchResult(new SearchResult(filteredResults));
          setRequestState(RequestState.SUCCEEDED);
        }).catch(error => {
          setRequestState(RequestState.FAILED);
          console.error('error fetching collection items:\n', error);
        });
      }
    } else if (isFailed(collectionItemsRequestState)) {
      setRequestState(RequestState.FAILED);
    }
  }, [collectionItemsData, collectionItemsRequestState, collectionOfferingIds, filters, isAdminPreview, language, offset]);
  const renderedCollectionTitle = useMemo(() => {
    if (!requestSucceeded || !collectionListing) return null;
    return interpolate(collectionListing.title, {
      count: searchResultTotal
    });
  }, [requestSucceeded, collectionListing, searchResultTotal]);
  const renderEmptyState = useCallback(() => {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(H4, {
        children: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: "appMarketplace.storefront.results.resultsPage.noResultsEmptyState.filterTitle",
          options: {
            pageHeading: renderedCollectionTitle
          }
        })
      }), /*#__PURE__*/_jsx("p", {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "appMarketplace.storefront.results.resultsPage.noResultsEmptyState.message"
        })
      })]
    });
  }, [renderedCollectionTitle]);
  useEffect(() => {
    if (requestSucceeded) {
      if (onReady) {
        onReady({
          receiveMessage: () => {}
        });
      }
      document.title = documentTitle;
    }
  }, [onReady, documentTitle, requestSucceeded]);
  useEffect(() => {
    if (requestFulfilled && !pageViewTracked && !isAdminPreview) {
      trackPageView({
        pageNumber: activePage,
        showRecommended: hasRecommendations,
        recommendationType: recommendationTypes,
        totalResults: searchResultTotal
      });
      setPageViewTracked(true);
    }
    if (requestSucceeded && !searchResultTotal && !isAdminPreview) {
      trackInteraction('no results returned');
    }
  }, [activePage, hasRecommendations, isAdminPreview, pageViewTracked, recommendationTypes, requestFulfilled, requestSucceeded, searchResultTotal, trackInteraction, trackPageView]);
  useEffect(() => {
    if (collectionSlug !== prevCollectionSlug || page !== prevPage) {
      setPageViewTracked(false);
    }
  }, [collectionSlug, page, prevCollectionSlug, prevPage]);
  const socialImageUrl = useSocialScreenshotUrl();
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [!isAdminPreview && /*#__PURE__*/_jsx(PublicOnly, {
      children: /*#__PURE__*/_jsx(DocumentHead, {
        children: /*#__PURE__*/_jsxs(Fragment, {
          children: [/*#__PURE__*/_jsx(CanonicalLink, {
            to: canonicalUrlPath
          }), hasPrevPage && /*#__PURE__*/_jsx(CanonicalLink, {
            rel: "prev",
            to: getPathPagination(canonicalUrlPath, activePage - 1)
          }), hasNextPage && /*#__PURE__*/_jsx(CanonicalLink, {
            rel: "next",
            to: getPathPagination(canonicalUrlPath, activePage + 1)
          }), /*#__PURE__*/_jsx(MetaDescription, {
            content: metaDescription
          }), noIndexNoFollow && /*#__PURE__*/_jsx(MetaRobots, {
            directive: ['nofollow', 'noindex']
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "card"
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "site"
          }), /*#__PURE__*/_jsx(MetaTwitter, {
            name: "image",
            content: socialImageUrl
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "type"
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "title",
            content: documentTitle
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "url",
            content: getFullHref(url)
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "description",
            content: metaDescription
          }), /*#__PURE__*/_jsx(MetaOpenGraph, {
            property: "image",
            content: socialImageUrl
          }), /*#__PURE__*/_jsx(PrerenderReady, {
            ready: requestFulfilled,
            statusCode: prerenderStatusCode
          })]
        })
      })
    }), /*#__PURE__*/_jsx(LoadingWrapper, {
      ErrorComponent: ErrorComponent,
      LoadingComponent: LoadingComponent,
      requestStatus: requestState,
      children: /*#__PURE__*/_jsx(NavMarker, {
        name: "STOREFRONT_LOAD",
        children: /*#__PURE__*/_jsx(ResultsPage, {
          activePage: activePage,
          description: /*#__PURE__*/_jsx(SanitizedText, {
            body: collectionListing ? collectionListing.description : '',
            WrapperComponent: "span"
          }),
          emptyStateComponent: renderEmptyState,
          filterable: true,
          filters: filters,
          offset: offset,
          onClearFilters: clearFilters,
          onFilterChange: updateFilter,
          onPageChange: handlePageChange,
          onResultClick: handleResultClick,
          onSortChange: onSortChange,
          onUpdateFilters: updateAllFilters,
          requestState: requestState,
          resultsPerPage: SEARCH_LENGTH,
          searchResult: searchResult,
          sortable: true,
          title: renderedCollectionTitle,
          trackInteraction: trackInteraction
        })
      })
    })]
  });
};
export default UIEmbeddedComponent(StorefrontCollectionContainer, {
  enableTopLevelEmbed: true
});